import React, { DragEvent, useMemo, memo, TouchEvent } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { css, CSSObject, SerializedStyles } from '@emotion/core'
import { ToggleButton } from '@/components/molecules/settings/toggle-button'
import { draggableIconStyle } from '@/components/molecules/settings/use-draggable-dom'
import { Hint } from '@/components/atoms/hint'
import { SearchItem, SearchSetting } from '@/models/search-setting'

export interface SearchItemsSectionProps {
  searchItems: SearchSetting[]
  onToggle: (index: number, item: SearchSetting) => Promise<void>
  onDragStart: (e: DragEvent<HTMLElement>) => void
  onDragOver: (e: DragEvent<HTMLElement>) => void
  onDrop: (e: DragEvent<HTMLElement>) => void
  onTouchStart: (e: TouchEvent<HTMLElement>) => void
  onTouchMove: (e: TouchEvent<HTMLElement>) => void
  onTouchEnd: (e: TouchEvent<HTMLElement>) => void
  title?: string
  hint?: string
  containerStyle?: CSSObject | SerializedStyles
  // カスタマイズ用のprops
  customTitleStyle?: SerializedStyles
  customHintStyle?: SerializedStyles
  customToggleLabelStyle?: SerializedStyles
}

const searchItemLabel = {
  [SearchItem.Telephone]: 'Phone number',
  [SearchItem.ReservationId]: 'Reservation ID',
  [SearchItem.GuestName]: 'Guest name',
  [SearchItem.GuestNameKana]: 'Guest name (Furigana)',
  [SearchItem.QR]: 'QR code',
  [SearchItem.RoomKey]: 'Room / Key number',
}

/**
 * 検索項目セクションコンポーネント
 * Self Checkout と Self Checkin の検索項目表示を統一
 */
const SearchItemsSectionComponent: React.FC<SearchItemsSectionProps> = ({
  searchItems,
  onToggle,
  onDragStart,
  onDragOver,
  onDrop,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  title = 'Reservation search criteria',
  hint = 'Select the search criteria to use at checkout and set the display order.',
  containerStyle,
  customTitleStyle,
  customHintStyle,
  customToggleLabelStyle,
}) => {
  const { t } = useTranslation()

  const mergedHintStyle = useMemo(() => css([hintStyle, customHintStyle]), [customHintStyle])

  return (
    <div css={[tileContainerStyle, containerStyle]}>
      <div css={headerStyle}>
        <div className="title" css={[titleStyle, customTitleStyle]}>
          {t(title)}
        </div>
        <Hint customCss={mergedHintStyle}>{t(hint)}</Hint>
      </div>
      <div>
        {searchItems.map(item => (
          <div
            key={item.inputType}
            id={item.inputType}
            draggable={true}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            css={draggableIconStyle}
            className="toggle-block"
          >
            <div css={[toggleLabelStyle, customToggleLabelStyle]}>
              <Trans i18nKey={t(searchItemLabel[item.inputType])} />
            </div>
            <ToggleButton
              value={item.isEnabled}
              onChange={async () => {
                await onToggle(searchItems.indexOf(item), item)
              }}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

export const SearchItemsSection = memo(SearchItemsSectionComponent, (prevProps, nextProps) => {
  return (
    prevProps.searchItems === nextProps.searchItems &&
    prevProps.onToggle === nextProps.onToggle &&
    prevProps.onDragStart === nextProps.onDragStart &&
    prevProps.onDragOver === nextProps.onDragOver &&
    prevProps.onDrop === nextProps.onDrop &&
    prevProps.onTouchStart === nextProps.onTouchStart &&
    prevProps.onTouchMove === nextProps.onTouchMove &&
    prevProps.onTouchEnd === nextProps.onTouchEnd &&
    prevProps.title === nextProps.title &&
    prevProps.hint === nextProps.hint &&
    prevProps.containerStyle === nextProps.containerStyle &&
    prevProps.customTitleStyle === nextProps.customTitleStyle &&
    prevProps.customHintStyle === nextProps.customHintStyle &&
    prevProps.customToggleLabelStyle === nextProps.customToggleLabelStyle
  )
})

const hintStyle = css({
  img: { width: 18 },
  '.help-popup': {
    color: '#676767',
    fontSize: 12,
    left: 23,
    top: 0,
    whiteSpace: 'nowrap',
    padding: 16,
    lineHeight: 1.5,
    fontWeight: 'bold',
  },
})

const headerStyle = css({
  padding: '0 32px',
  backgroundColor: 'rgb(245, 245, 245)',
  height: 32,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
})

const titleStyle = css({
  fontWeight: 'bold',
  fontSize: 12,
  color: 'rgb(103, 103, 103)',
})

const tileContainerStyle = css({
  '.toggle-block': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: '1px solid rgb(242, 242, 242)',
    ':first-of-type': { borderTop: 'none' },
    height: 62,
    paddingRight: 32,
    paddingLeft: 64,
  },
})

const toggleLabelStyle = css({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 'bold',
  fontSize: 14,
  p: { fontWeight: 'normal' },
})
